/**
 * 1 星 Star Battle 求解器。
 *
 * 设计依据（直接由规则推导，不基于任何既有实现）：
 * 1 星时每行恰好一颗星，故解可完整表示为一个数组 col[r] = 第 r 行星星所在列。
 * 三条规则退化为：
 *   ① 每列恰好一颗   → col 各元素互不相同（是 0..dim-1 的一个排列）
 *   ② 每区恰好一颗   → 各 (r, col[r]) 落在互不相同的区域；因此区域数必须等于 dim
 *   ③ 八向不相邻     → 同行不可能有两颗；相隔 ≥2 行的两颗行距已 ≥2；
 *                      故只需约束相邻行：|col[r] - col[r-1]| ≥ 2
 *
 * 于是问题化为「带禁位与相邻行间距约束的排列枚举」，按行深搜即可，
 * 无需通用回溯框架、约束传播或记忆化。
 *
 * 剪枝：每落一子就检查所有未用区域是否仍有可落位（剩余行 × 未用列），
 * 一旦某区域已无处可放即回溯。对 dim ≤ 25 足够。
 */

import { countRegions } from './sbn.js';

/**
 * @typedef {object} SolveResult
 * @property {number[][]} solutions 每个解为长度 dim 的数组，元素是该行星星的列号
 * @property {boolean} unique 是否恰好一个解
 * @property {number} nodes 搜索节点数（用于性能观察）
 */

/**
 * 求 1 星谜题的解，最多找 limit 个（判唯一性只需 2）。
 *
 * @param {number[][]} regionGrid 区域 id 网格，id 从 1 起连续
 * @param {object} [opts]
 * @param {number} [opts.limit=2] 找到这么多解就停
 * @returns {SolveResult}
 * @throws {RangeError} 区域数不等于 dim（1 星谜题无解的结构性原因）
 */
export function solveOneStar(regionGrid, opts = {}) {
    const limit = opts.limit ?? 2;
    const dim = regionGrid.length;
    const regionCount = countRegions(regionGrid);

    if (regionCount !== dim) {
        throw new RangeError(
            `1 星谜题要求区域数等于 dim：dim=${dim}，实际区域数=${regionCount}`);
    }

    // 每个区域的格子列表，用于可行性剪枝。regionCells[g] = [r*dim+c, ...]
    const regionCells = Array.from({ length: dim + 1 }, () => []);
    for (let r = 0; r < dim; r++) {
        for (let c = 0; c < dim; c++) regionCells[regionGrid[r][c]].push(r * dim + c);
    }

    const solutions = [];
    const col = new Int32Array(dim);
    let nodes = 0;

    /**
     * 未用区域是否都还有落点：存在某格 (rr,cc)，rr ≥ fromRow 且 cc 未用。
     * @param {number} fromRow @param {number} usedCols @param {number} usedRegions
     */
    function regionsStillFeasible(fromRow, usedCols, usedRegions) {
        for (let g = 1; g <= dim; g++) {
            if (usedRegions & (1 << (g - 1))) continue;
            let ok = false;
            for (const cell of regionCells[g]) {
                const rr = (cell / dim) | 0;
                if (rr < fromRow) continue;
                if (usedCols & (1 << (cell % dim))) continue;
                ok = true;
                break;
            }
            if (!ok) return false;
        }
        return true;
    }

    /**
     * @param {number} r 当前待放置的行
     * @param {number} usedCols 已用列位掩码
     * @param {number} usedRegions 已用区域位掩码（区域 g 对应第 g-1 位）
     * @param {number} prevCol 上一行的列号，r===0 时传 -99 使间距约束自然成立
     */
    function dfs(r, usedCols, usedRegions, prevCol) {
        if (solutions.length >= limit) return;
        if (r === dim) {
            solutions.push(Array.from(col));
            return;
        }
        nodes++;

        for (let c = 0; c < dim; c++) {
            if (usedCols & (1 << c)) continue;              // ① 列已占
            if (Math.abs(c - prevCol) < 2) continue;         // ③ 与上一行相邻
            const g = regionGrid[r][c];
            const gBit = 1 << (g - 1);
            if (usedRegions & gBit) continue;                // ② 区域已占

            const nextCols = usedCols | (1 << c);
            const nextRegions = usedRegions | gBit;
            if (!regionsStillFeasible(r + 1, nextCols, nextRegions)) continue;

            col[r] = c;
            dfs(r + 1, nextCols, nextRegions, c);
            if (solutions.length >= limit) return;
        }
    }

    dfs(0, 0, 0, -99);

    return { solutions, unique: solutions.length === 1, nodes };
}

/**
 * 解（列号数组）→ 布尔星盘，便于渲染与逐格比对。
 * @param {number[]} solution
 * @returns {boolean[][]}
 */
export function solutionToGrid(solution) {
    const dim = solution.length;
    return Array.from({ length: dim }, (_, r) =>
        Array.from({ length: dim }, (_, c) => solution[r] === c));
}

/**
 * 通用 N 星求解入口。当前仅支持 1 星（StarDoku 全部关卡均为 1 星）。
 * @param {number[][]} regionGrid
 * @param {number} stars
 * @param {object} [opts]
 * @returns {SolveResult}
 */
export function solve(regionGrid, stars, opts) {
    if (stars !== 1) {
        throw new RangeError(
            `暂不支持 ${stars} 星谜题（当前仅实现 1 星，见 solver.js 文件头）`);
    }
    return solveOneStar(regionGrid, opts);
}
